<script setup lang="ts">
/**
 * A layer's shape, drawn as an outline: a triangle for the company, a circle for a team, a five-pointed star for a
 * member, a hexagon for a role, a square for a task, a parallelogram for an execution, a ring for a run. Plain
 * shapes, never pictures. SVG rather than borders, because a border cannot follow a clip-path.
 * Decoration: the layer's name beside it is what a screen reader hears.
 */
import type { LayerShape } from '../lib/surfaces'

defineProps<{ shape: LayerShape }>()

const POLYGON = {
    triangle: '14,2.5 26,25 2,25',
    star: '14,2.2 17.1,10.9 26.4,11.2 19,16.8 21.6,25.7 14,20.5 6.4,25.7 9,16.8 1.6,11.2 10.9,10.9',
    hexagon: '7.5,2.5 20.5,2.5 27,14 20.5,25.5 7.5,25.5 1,14',
    parallelogram: '8.5,4 27,4 19.5,24 1,24',
} as const

function isPolygon(shape: LayerShape): shape is keyof typeof POLYGON {
    return shape in POLYGON
}
</script>

<template>
    <svg class="zh-layer-icon" viewBox="0 0 28 28" aria-hidden="true">
        <polygon v-if="isPolygon(shape)" :points="POLYGON[shape]" />
        <circle v-else-if="shape === 'circle'" cx="14" cy="14" r="12.5" />
        <template v-else-if="shape === 'ring'">
            <circle cx="14" cy="14" r="12.5" />
            <circle cx="14" cy="14" r="6.5" />
        </template>
        <rect v-else x="1.5" y="1.5" width="25" height="25" />
    </svg>
</template>

<style scoped>
.zh-layer-icon {
    display: block;
    width: 1.25rem;
    height: 1.25rem;
    overflow: visible;
}

.zh-layer-icon * {
    fill: var(--void-black);
    stroke: var(--line-strong);
    stroke-width: 1.5;
    stroke-linejoin: round;
}
</style>
